import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Form, 
  Space, 
  message, 
  Tabs, 
  Row,
  Col,
  Statistic,
  Divider,
  Progress,
  Tag,
  List,
  Avatar,
  Badge,
  Tooltip,
  Alert,
  Collapse,
  Timeline,
  Select,
  Input,
  DatePicker,
  Popconfirm,
  Table,
  Steps,
  Radio,
  Checkbox,
  Empty
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  PlusOutlined,
  ArrowLeftOutlined,
  TrophyOutlined,
  StarOutlined,
  FireOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  BookOutlined,
  AimOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  FileTextOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { riskConfig } from '../config/risk';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { Group: RadioGroup } = Radio;

const Risk = () => {
  const navigate = useNavigate();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('risks');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [riskForm] = Form.useForm();
  const [monitoringForm] = Form.useForm();
  const [reviewForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 加载风险数据
  const fetchRisks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(riskConfig.apiEndpoints.risks);
      if (response.data.success) {
        setRisks(response.data.risks || []);
      } else {
        message.error(response.data.message || '获取风险数据失败');
      }
    } catch (error) {
      console.error('获取风险数据失败:', error);
      message.error('获取风险数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开风险弹窗
  const handleOpenRisk = (risk = null) => {
    setSelectedRisk(risk);
    if (risk) {
      // 编辑现有风险
      riskForm.setFieldsValue({
        name: risk.name,
        type: risk.type,
        probability: risk.probability,
        impact: risk.impact,
        status: risk.status,
        strategy: risk.strategy,
        description: risk.description
      });
    } else {
      // 新建风险
      riskForm.resetFields();
    }
    setShowRiskModal(true);
  };

  // 保存风险
  const handleSaveRisk = async (values) => {
    try {
      const url = selectedRisk 
        ? `${riskConfig.apiEndpoints.update}/${selectedRisk.id}`
        : riskConfig.apiEndpoints.create;
      
      const method = selectedRisk ? 'PUT' : 'POST';
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        name: values.name,
        type: values.type,
        probability: values.probability,
        impact: values.impact,
        status: values.status,
        strategy: values.strategy,
        description: values.description
      };
      
      let response;
      if (selectedRisk) {
        response = await axios.put(`${riskConfig.apiEndpoints.update}/${selectedRisk.id}`, requestBody);
      } else {
        response = await axios.post(riskConfig.apiEndpoints.create, requestBody);
      }

      if (response.data.success) {
        message.success(selectedRisk ? '风险更新成功' : '风险创建成功');
        setShowRiskModal(false);
        fetchRisks();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存风险失败:', error);
      message.error('操作失败');
    }
  };

  // 删除风险
  const handleDeleteRisk = async (riskId) => {
    try {
      const response = await axios.delete(`${riskConfig.apiEndpoints.delete}/${riskId}`);
      if (response.data.success) {
        message.success('风险删除成功');
        fetchRisks();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除风险失败:', error);
      message.error('删除失败');
    }
  };

  // 打开监控弹窗
  const handleOpenMonitoring = (risk) => {
    setSelectedRisk(risk);
    monitoringForm.resetFields();
    setShowMonitoringModal(true);
  };

  // 保存监控记录
  const handleSaveMonitoring = async (values) => {
    try {
      const requestBody = {
        risk_id: selectedRisk.id,
        content: values.content,
        status: values.status,
        notes: values.notes
      };
      
      const response = await axios.post(riskConfig.apiEndpoints.addMonitoring, requestBody);

      if (response.data.success) {
        message.success('监控记录成功');
        setShowMonitoringModal(false);
        fetchRisks();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存监控记录失败:', error);
      message.error('操作失败');
    }
  };

  // 打开复盘弹窗
  const handleOpenReview = (risk) => {
    setSelectedRisk(risk);
    reviewForm.resetFields();
    setShowReviewModal(true);
  };

  // 保存复盘记录
  const handleSaveReview = async (values) => {
    try {
      const requestBody = {
        risk_id: selectedRisk.id,
        content: values.content,
        lessons: values.lessons,
        improvements: values.improvements,
        notes: values.notes
      };
      
      const response = await axios.post(riskConfig.apiEndpoints.addReview, requestBody);

      if (response.data.success) {
        message.success('复盘记录成功');
        setShowReviewModal(false);
        fetchRisks();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存复盘记录失败:', error);
      message.error('操作失败');
    }
  };

  // 计算风险等级
  const calculateRiskLevel = (probability, impact) => {
    const riskValue = probability * impact;
    if (riskValue <= 4) return 'very_low';
    if (riskValue <= 8) return 'low';
    if (riskValue <= 12) return 'medium';
    if (riskValue <= 16) return 'high';
    return 'very_high';
  };

  // 获取风险等级颜色
  const getRiskLevelColor = (probability, impact) => {
    const level = calculateRiskLevel(probability, impact);
    const levelItem = riskConfig.riskLevels.find(l => l.key === level);
    return levelItem ? levelItem.color : '#D9D9D9';
  };

  // 获取风险等级名称
  const getRiskLevelName = (probability, impact) => {
    const level = calculateRiskLevel(probability, impact);
    const levelItem = riskConfig.riskLevels.find(l => l.key === level);
    return levelItem ? levelItem.name : '未知';
  };

  // 获取风险类型颜色
  const getTypeColor = (type) => {
    const typeItem = riskConfig.riskTypes.find(t => t.key === type);
    return typeItem ? typeItem.color : '#D9D9D9';
  };

  // 获取风险状态颜色
  const getStatusColor = (status) => {
    const statusItem = riskConfig.riskStatus.find(s => s.key === status);
    return statusItem ? statusItem.color : '#D9D9D9';
  };

  // 处理评估问卷答案
  const handleAssessmentAnswer = (questionId, value) => {
    console.log('评估答案更新:', questionId, value);
    setAssessmentAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // 提交评估问卷
  const handleSubmitAssessment = async () => {
    try {
      const totalScore = Object.values(assessmentAnswers).reduce((sum, score) => sum + score, 0);
      
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        answers: assessmentAnswers,
        total_score: totalScore,
        assessment_date: dayjs().format('YYYY-MM-DD')
      };
      
      const response = await axios.post(riskConfig.apiEndpoints.submitAssessment, requestBody);

      if (response.data.success) {
        message.success('评估提交成功');
        setShowAssessmentModal(false);
        setAssessmentAnswers({});
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('提交评估失败:', error);
      message.error('操作失败');
    }
  };

  // 获取评估结果
  const getAssessmentResult = () => {
    const totalScore = Object.values(assessmentAnswers).reduce((sum, score) => sum + score, 0);
    console.log('评估总分:', totalScore, '答案:', assessmentAnswers);
    return riskConfig.riskAssessment.results.find(result => {
      const [min, max] = result.range.split('-').map(Number);
      return totalScore >= min && totalScore <= max;
    });
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchRisks();
  }, []);

  // 按风险等级统计
  const levelCount = (keys) =>
    risks.filter((r) => keys.includes(calculateRiskLevel(r.probability, r.impact))).length;

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
            {riskConfig.name}
          </div>
          <div className="mod-header-subtitle">
            识别、评估与监控项目风险，制定应对策略，守住经营底线
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setShowTypeModal(true)}
            style={{ borderColor: riskConfig.bgColor, color: riskConfig.bgColor }}
          >
            风险类型
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={() => setShowAssessmentModal(true)}
            style={{ borderColor: riskConfig.bgColor, color: riskConfig.bgColor }}
          >
            风险评估
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
            style={{ borderColor: riskConfig.bgColor, color: riskConfig.bgColor }}
          >
            风险TIPS
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenRisk()}
            style={{ backgroundColor: riskConfig.bgColor, borderColor: riskConfig.bgColor }}
          >
            新增风险
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366F1' }}>
            <BarChartOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{risks.length}</div>
            <div className="mod-stat-label">风险总数</div>
          </div>
        </div>
        <div className="mod-stat-card danger">
          <div className="mod-stat-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
            <FireOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{levelCount(['high', 'very_high'])}</div>
            <div className="mod-stat-label">高风险</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>
            <ExclamationCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{levelCount(['medium'])}</div>
            <div className="mod-stat-label">中风险</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{levelCount(['low', 'very_low'])}</div>
            <div className="mod-stat-label">低风险</div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'risks',
              label: (
                <span>
                  <ExclamationCircleOutlined />
                  风险列表
                </span>
              ),
              children: (
                <Row gutter={[16, 16]}>
                  {risks.map((risk) => (
                    <Col xs={24} md={12} lg={8} key={risk.id}>
                      <Card
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{risk.name}</span>
                            <Space>
                              <Tag color={getRiskLevelColor(risk.probability, risk.impact)}>
                                {getRiskLevelName(risk.probability, risk.impact)}
                              </Tag>
                              <Tag color={getStatusColor(risk.status)}>
                                {riskConfig.riskStatus.find(s => s.key === risk.status)?.name}
                              </Tag>
                            </Space>
                          </div>
                        }
                        extra={
                          <Space>
                            <Button 
                              type="text" 
                              icon={<EyeOutlined />}
                              onClick={() => handleOpenMonitoring(risk)}
                              size="small"
                            />
                            <Button 
                              type="text" 
                              icon={<HistoryOutlined />}
                              onClick={() => handleOpenReview(risk)}
                              size="small"
                            />
                            <Button 
                              type="text" 
                              icon={<EditOutlined />}
                              onClick={() => handleOpenRisk(risk)}
                              size="small"
                            />
                            <Popconfirm
                              title="确定要删除这个风险吗？"
                              onConfirm={() => handleDeleteRisk(risk.id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button 
                                type="text" 
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                              />
                            </Popconfirm>
                          </Space>
                        }
                        style={{ 
                          height: '100%',
                          borderRadius: '16px',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                          background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                          border: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        bodyStyle={{ padding: '6px' }}
                        hoverable
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                        }}
                      >
                        <div style={{ marginBottom: '12px' }}>
                          <Text type="secondary">{risk.description}</Text>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <Tag color={getTypeColor(risk.type)}>
                            {riskConfig.riskTypes.find(t => t.key === risk.type)?.name}
                          </Tag>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <Row gutter={8}>
                            <Col span={12}>
                              <Text strong>概率:</Text>
                              <Text type="secondary">
                                {riskConfig.probabilityLevels.find(p => p.key === risk.probability)?.name}
                              </Text>
                            </Col>
                            <Col span={12}>
                              <Text strong>影响:</Text>
                              <Text type="secondary">
                                {riskConfig.impactLevels.find(i => i.key === risk.impact)?.name}
                              </Text>
                            </Col>
                          </Row>
                        </div>
                        
                        {risk.strategy && (
                          <div style={{ marginBottom: '12px' }}>
                            <Text strong>应对策略:</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {risk.strategy}
                            </Text>
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                  
                  {risks.length === 0 && (
                    <Col span={24}>
                      <Card style={{ borderRadius: '16px' }}>
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <span style={{ color: '#94a3b8' }}>
                              暂无风险记录，点击"新增风险"开始记录您的风险
                            </span>
                          }
                          style={{ padding: '40px 0' }}
                        />
                      </Card>
                    </Col>
                  )}
                </Row>
              )
            }
          ]}
        />
      </div>

      {/* 风险弹窗 */}
      {showRiskModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {selectedRisk ? '编辑风险' : '新增风险'}
              </h3>
              <Button
                type="text"
                onClick={() => setShowRiskModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
      >
        <Form
          form={riskForm}
          layout="vertical"
          onFinish={handleSaveRisk}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="风险名称"
                rules={[{ required: true, message: '请输入风险名称' }]}
              >
                <Input placeholder="请输入风险名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="风险类型"
                rules={[{ required: true, message: '请选择风险类型' }]}
              >
                <Select placeholder="请选择风险类型">
                  {riskConfig.riskTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      {type.icon} {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="probability"
                label="风险概率"
                rules={[{ required: true, message: '请选择风险概率' }]}
              >
                <Select placeholder="请选择概率">
                  {riskConfig.probabilityLevels.map(level => (
                    <Option key={level.key} value={level.key}>
                      {level.name} - {level.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="impact"
                label="风险影响"
                rules={[{ required: true, message: '请选择风险影响' }]}
              >
                <Select placeholder="请选择影响">
                  {riskConfig.impactLevels.map(level => (
                    <Option key={level.key} value={level.key}>
                      {level.name} - {level.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="风险状态"
                rules={[{ required: true, message: '请选择风险状态' }]}
              >
                <Select placeholder="请选择状态">
                  {riskConfig.riskStatus.map(status => (
                    <Option key={status.key} value={status.key}>
                      {status.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="风险描述"
            rules={[{ required: true, message: '请输入风险描述' }]}
          >
            <TextArea rows={3} placeholder="请详细描述风险情况" />
          </Form.Item>
          
          <Form.Item
            name="strategy"
            label="应对策略"
          >
            <TextArea rows={3} placeholder="请描述风险应对策略" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowRiskModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => riskForm.submit()}>
                {selectedRisk ? '更新风险' : '创建风险'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 监控弹窗 */}
      {showMonitoringModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                监控记录 - {selectedRisk?.name}
              </h3>
              <Button
                type="text"
                onClick={() => setShowMonitoringModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
      >
        <Form
          form={monitoringForm}
          layout="vertical"
          onFinish={handleSaveMonitoring}
        >
          <Form.Item
            name="content"
            label="监控内容"
            rules={[{ required: true, message: '请输入监控内容' }]}
          >
            <TextArea rows={4} placeholder="请详细描述监控情况" />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="当前状态"
            rules={[{ required: true, message: '请选择当前状态' }]}
          >
            <Select placeholder="请选择状态">
              {riskConfig.riskStatus.map(status => (
                <Option key={status.key} value={status.key}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowMonitoringModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => monitoringForm.submit()}>
                保存监控
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 复盘弹窗 */}
      {showReviewModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                复盘记录 - {selectedRisk?.name}
              </h3>
              <Button
                type="text"
                onClick={() => setShowReviewModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
      >
        <Form
          form={reviewForm}
          layout="vertical"
          onFinish={handleSaveReview}
        >
          <Form.Item
            name="content"
            label="复盘内容"
            rules={[{ required: true, message: '请输入复盘内容' }]}
          >
            <TextArea rows={4} placeholder="请详细描述风险事件和处理过程" />
          </Form.Item>
          
          <Form.Item
            name="lessons"
            label="经验教训"
            rules={[{ required: true, message: '请输入经验教训' }]}
          >
            <TextArea rows={3} placeholder="请总结经验教训" />
          </Form.Item>
          
          <Form.Item
            name="improvements"
            label="改进措施"
          >
            <TextArea rows={3} placeholder="请提出改进措施" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowReviewModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => reviewForm.submit()}>
                保存复盘
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 风险评估弹窗 */}
      {showAssessmentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                风险承受能力评估
              </h3>
              <Button
                type="text"
                onClick={() => setShowAssessmentModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
      >
        <div style={{ padding: '16px 0' }}>
          <Alert
            message="评估说明"
            description="通过以下问题评估您的风险承受能力，帮助制定合适的风险管理策略"
            type="info"
            style={{ marginBottom: '24px' }}
          />
          
          {riskConfig.riskAssessment.questions.map((question) => (
            <Card key={question.id} style={{ marginBottom: '16px' }}>
              <Title level={5}>{question.question}</Title>
              <RadioGroup
                onChange={(e) => handleAssessmentAnswer(question.id, parseInt(e.target.value))}
                value={assessmentAnswers[question.id]}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {question.options.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.text}
                    </Radio>
                  ))}
                </Space>
              </RadioGroup>
            </Card>
          ))}
          
          {Object.keys(assessmentAnswers).length === riskConfig.riskAssessment.questions.length && (
            <Card style={{ marginTop: '16px' }}>
              <Title level={4}>评估结果</Title>
              {(() => {
                const result = getAssessmentResult();
                return result ? (
                  <div>
                    <Tag color={result.color} style={{ fontSize: '16px', padding: '8px 16px' }}>
                      {result.level}
                    </Tag>
                    <Text style={{ display: 'block', marginTop: '12px' }}>
                      {result.description}
                    </Text>
                  </div>
                ) : null;
              })()}
            </Card>
          )}
          
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <Space>
              <Button 
                type="primary" 
                onClick={handleSubmitAssessment}
                disabled={Object.keys(assessmentAnswers).length !== riskConfig.riskAssessment.questions.length}
              >
                提交评估
              </Button>
              <Button onClick={() => setShowAssessmentModal(false)}>
                取消
              </Button>
            </Space>
          </div>
        </div>
          </div>
        </div>
      )}

      {/* 风险TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <BulbOutlined style={{ color: riskConfig.bgColor, marginRight: '8px' }} />
                风险管理TIPS
              </h3>
              <Button
                type="text"
                onClick={() => setShowTipsModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
      >
        <div style={{ padding: '16px 0' }}>
          {/* 风险管理流程 */}
          <Card title="风险管理五大流程" style={{ marginBottom: '16px' }}>
            <Steps direction="vertical" size="small">
              {riskConfig.riskManagementProcess.process.map((step, index) => (
                <Step
                  key={index}
                  title={step.title}
                  description={step.description}
                  icon={<span style={{ fontSize: '16px' }}>{step.icon}</span>}
                />
              ))}
            </Steps>
          </Card>

          {/* 风险管理TIPS */}
          <Card title="风险管理TIPS" style={{ marginBottom: '16px' }}>
            <Timeline>
              {riskConfig.riskManagementTips.tips.map((tip, index) => (
                <Timeline.Item key={index}>
                  <Title level={5}>{tip.title}</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                    {tip.description}
                  </Text>
                  <div style={{ 
                    backgroundColor: '#f6f6f6', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    示例: {tip.example}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* 风险应对策略 */}
          <Card title="风险应对策略">
            <Row gutter={[16, 16]}>
              {riskConfig.riskResponseStrategies.strategies.map((strategy, index) => (
                <Col xs={24} md={12} key={index}>
                  <div style={{ 
                    border: '1px solid #f0f0f0', 
                    borderRadius: '8px', 
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '12px' 
                    }}>
                      <span style={{ fontSize: '20px', marginRight: '8px' }}>{strategy.icon}</span>
                      <Title level={5} style={{ margin: 0 }}>
                        {strategy.title}
                      </Title>
                    </div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                      {strategy.description}
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                      {strategy.examples.map((example, i) => (
                        <li key={i}>
                          <Text style={{ fontSize: '12px' }}>{example}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Risk;
